import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { useEffect, useMemo, useRef, useState } from "react";
import { TextInput } from "react-native";
import { useFragment } from "react-relay";

import { Button } from "@whoosh/design-system/src/components/controls";
import { SubtlePhoneInput } from "@whoosh/design-system/src/components/controls/PhoneInput";
import { buildFullPhoneNumber } from "@whoosh/design-system/src/components/controls/PhoneInput/utils/buildPhoneNumber";
import { getCountryCodeById } from "@whoosh/design-system/src/components/controls/PhoneInput/utils/countryCodes";
import { SubtleInput } from "@whoosh/design-system/src/components/controls/SubtleInput";
import {
  Box,
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { isVerifyingEmailOrPhoneAtom } from "../../atoms/stores";
import { useCheckEmailorPhoneNotifications } from "../../hooks/useCheckEmailOrPhoneNotifications";
import { InitialStepFragment_Session$key } from "./__generated__/InitialStepFragment_Session.graphql";
import { isValidToContinue } from "./utils";

type InitialStepProps = {
  fragRef: InitialStepFragment_Session$key;
};

export const InitialStep = ({ fragRef }: InitialStepProps) => {
  const data = useFragment(InitialStepFragmentString, fragRef);
  const user = data.user;
  const verifyingEmailOrPhone = useAtomValue(isVerifyingEmailOrPhoneAtom);

  const startingValueMemo = useMemo(() => {
    return verifyingEmailOrPhone === "email"
      ? user?.email || ""
      : verifyingEmailOrPhone === "phone"
      ? user?.phoneNumber?.nationalNumber || ""
      : "";
  }, [user?.email, user?.phoneNumber?.nationalNumber, verifyingEmailOrPhone]);

  const [currentValue, setCurrentValue] = useState(startingValueMemo);

  useEffect(() => {
    setCurrentValue(startingValueMemo);
  }, [startingValueMemo]);

  const defaultCountryCodeId =
    data.currentClientProfile?.member?.club?.defaultCountryCode || "US";
  const defaultCountryCode = getCountryCodeById(defaultCountryCodeId);

  const [currentCountryCode, setCurrentCountryCode] = useState(
    user?.phoneNumber?.countryCode.toString() ||
      defaultCountryCode?.code?.toString() ||
      "1"
  );
  const canContinue = isValidToContinue(currentValue, currentCountryCode);

  const [checkContactInfo, isChecking] = useCheckEmailorPhoneNotifications();
  const handleContinueClick = () => {
    if (canContinue && !isChecking) {
      checkContactInfo({
        value:
          verifyingEmailOrPhone === "phone"
            ? buildFullPhoneNumber(currentCountryCode, currentValue) || ""
            : currentValue,
        type: verifyingEmailOrPhone === "phone" ? "Phone" : "Email",
      });
    }
  };

  const inputRef = useRef<TextInput>(null);
  useEffect(() => {
    // Automatically focus the input after swapping
    // this is necessary so that when we flip between phone/email, we don't lose focus.
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [verifyingEmailOrPhone]);
  const isVerifyingEmailOrPhone = useAtomValue(isVerifyingEmailOrPhoneAtom);
  return (
    <VStack flexGrow={1}>
      <Text variant={"mainTitle2"} pt={10}>
        {isVerifyingEmailOrPhone
          ? `${t`Verify your`} ${
              isVerifyingEmailOrPhone === "email" ? t`email` : t`phone number`
            }`
          : t`Notifications`}
      </Text>
      <VStack flexGrow={1} pt={8}>
        {verifyingEmailOrPhone === "phone" ? (
          <SubtlePhoneInput
            placeholder={t`phone`}
            nationalNumber={currentValue}
            defaultCountryId={currentCountryCode}
            onChange={({ countryCode, nationalNumber }) => {
              setCurrentCountryCode(countryCode);
              setCurrentValue(nationalNumber);
            }}
            name={"phone"}
            inputRef={inputRef}
          />
        ) : (
          <SubtleInput
            placeholder={t`Email`}
            name={"Email"}
            value={currentValue}
            onChange={setCurrentValue}
            inputRef={inputRef}
          />
        )}
      </VStack>
      <Box height={120} />
      <VStack gap={6}>
        {verifyingEmailOrPhone !== "email" ? (
          <HStack justifyContent={"center"}>
            <Text
              variant={"mainSmallMedium"}
              color="text-secondary"
            >{t`Use mobile phone number only`}</Text>
          </HStack>
        ) : null}
        <Button
          isLoading={isChecking}
          isDisabled={!canContinue}
          onPress={handleContinueClick}
          accessibilityLabel={t`Continue button`}
        >
          {t`Continue`}
        </Button>
      </VStack>
    </VStack>
  );
};

export const InitialStepFragmentString = graphql`
  fragment InitialStepFragment_Session on Session {
    currentClientProfile {
      member {
        club {
          defaultCountryCode
        }
      }
    }
    user {
      ... on MemberUser {
        email
        phoneNumber {
          countryCode
          nationalNumber
        }
      }
    }
  }
`;
